import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import {
    Train,
    Building2,
    Droplets,
    Zap,
    Map,
    Globe,
} from "lucide-react";

const PROGRAMME_THEMES = [
    {
        icon: Train,
        title: "Compact, transit-oriented urban growth",
        color: "#6bbac2",
    },
    {
        icon: Building2,
        title: "Integrated housing and infrastructure planning",
        color: "#bfbe28",
    },
    {
        icon: Droplets,
        title: "Climate-resilient water and wastewater services",
        color: "#6bbac2",
    },
    {
        icon: Zap,
        title: "Clean-energy and smart-grid transitions",
        color: "#bfbe28",
    },
    {
        icon: Map,
        title: "Evidence-driven spatial planning under evolving resource management frameworks",
        color: "#6bbac2",
    },
    {
        icon: Globe,
        title: "Trans-Tasman climate and digital innovation partnerships",
        color: "#bfbe28",
    },
];

function ThemeCard({ item, index, size }) {
    const Icon = item.icon;
    const isPrimary = item.color === "#6bbac2";

    return (
        <Col key={index} lg={4} md={6} className="mb-4">
            <Card
                className="programme-theme-card h-100"
                style={{
                    background: "rgba(255, 255, 255, 0.04)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    borderRadius: "20px",
                    padding: 0,
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = isPrimary
                        ? "rgba(107, 186, 194, 0.12)"
                        : "rgba(191, 190, 40, 0.1)";
                    e.currentTarget.style.borderColor = isPrimary
                        ? "rgba(107, 186, 194, 0.5)"
                        : "rgba(191, 190, 40, 0.5)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow = isPrimary
                        ? "0 24px 48px rgba(107, 186, 194, 0.25)"
                        : "0 24px 48px rgba(191, 190, 40, 0.22)";
                    const iconEl = e.currentTarget.querySelector(".programme-theme-icon-wrap");
                    if (iconEl) {
                        iconEl.style.transform = "scale(1.1) rotate(-3deg)";
                        iconEl.style.boxShadow = isPrimary
                            ? "0 12px 28px rgba(107, 186, 194, 0.4)"
                            : "0 12px 28px rgba(191, 190, 40, 0.4)";
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.08)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    const iconEl = e.currentTarget.querySelector(".programme-theme-icon-wrap");
                    if (iconEl) {
                        iconEl.style.transform = "scale(1) rotate(0deg)";
                        iconEl.style.boxShadow = "none";
                    }
                }}
            >
                <div
                    style={{
                        padding: size ? "2rem" : "1.5rem",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        height: "100%",
                    }}
                >
                    <div
                        className="programme-theme-icon-wrap"
                        style={{
                            width: size ? 72 : 64,
                            height: size ? 72 : 64,
                            borderRadius: "20px",
                            background: isPrimary
                                ? "linear-gradient(145deg, rgba(107, 186, 194, 0.28), rgba(107, 186, 194, 0.12))"
                                : "linear-gradient(145deg, rgba(191, 190, 40, 0.28), rgba(191, 190, 40, 0.12))",
                            border: `2px solid ${item.color}40`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "1.25rem",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                    >
                        <Icon
                            size={size ? 36 : 32}
                            strokeWidth={2}
                            style={{ color: item.color }}
                        />
                    </div>
                    <p
                        className="text-white mb-0"
                        style={{
                            fontSize: size ? "1.05rem" : "0.95rem",
                            lineHeight: "1.55",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                        }}
                    >
                        {item.title}
                    </p>
                </div>
                {/* Bottom accent line */}
                <div
                    style={{
                        height: "4px",
                        width: "100%",
                        background: `linear-gradient(90deg, ${item.color}, transparent)`,
                        opacity: 0.6,
                    }}
                />
            </Card>
        </Col>
    );
}

function ProgrammeThemesSection() {
    const size =
        document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

    return (
        <section
            className="section py-5"
            style={{
                background: "transparent",
                position: "relative",
                zIndex: 1,
            }}
        >
            <Container>
                <Row className="justify-content-center mb-5">
                    <Col md={10} lg={8} className="text-center">
                        <p
                            className="text-white mb-4"
                            style={{
                                fontSize: size ? "1.35rem" : "1.15rem",
                                lineHeight: "1.75",
                                opacity: 0.95,
                            }}
                        >
                            ANZ Digital Twin 2026 returns as a strategic platform connecting
                            infrastructure owners, urban planners, utilities, Māori and community
                            leaders, technologists and researchers from across Australia and New
                            Zealand.
                        </p>
                        <p
                            className="text-white mb-0"
                            style={{
                                fontSize: size ? "1.2rem" : "1.05rem",
                                lineHeight: "1.7",
                                color: "#6bbac2",
                                fontWeight: 600,
                                letterSpacing: "0.03em",
                            }}
                        >
                            The programme will explore how digital twins, AI and advanced data
                            infrastructure can enable:
                        </p>
                    </Col>
                </Row>
                <Row className="justify-content-center mb-5">
                    <Col xs={12}>
                        <img
                            src={require("assets/titleimages/1.jpg")}
                            alt="Conference"
                            style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: "320px",
                                objectFit: "cover",
                                borderRadius: "16px",
                                border: "1px solid rgba(255,255,255,0.1)",
                                boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
                            }}
                        />
                    </Col>
                </Row>
                <Row className="justify-content-center">
                    {PROGRAMME_THEMES.map((item, i) => (
                        <ThemeCard key={i} item={item} index={i} size={size} />
                    ))}
                </Row>
            </Container>
        </section>
    );
}

export default ProgrammeThemesSection;
